import { format } from "date-fns";
import { Download } from "lucide-react";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import Modal from "@/components/atomic/Modal";
import Button from "@/components/ui/Button";
import CategorySelectModal from "@/components/ui/CategorySelectModal";
import VocsView from "@/components/ui/VocsView";
import { useAnalyses } from "@/hooks/useAnalyses";
import { useAnalysisCategorySelect } from "@/hooks/useAnalysisCategorySelect";
import { useAnalysisVocs } from "@/hooks/useAnalysisVoc";
import { useDisplayGroups } from "@/hooks/useDisplayGroups";
import { useSummary } from "@/hooks/useSummary";
import { cn } from "@/lib/utils";
import { AnalysisService } from "@/services/analysis/analysis.service";
import { downloadCsv } from "@/utils/csv";
import { CategoryHeader } from "./CategoryHeader";
import { CategoryOverview } from "./CategoryOverview";
import { EmptyState } from "./EmptyState";
import { useHeightSync } from "./hooks/useHeightSync";
import { ProblemList } from "./ProblemList";
import { CategorySummaryProps } from "./types";

export const CategorySummary: FC<CategorySummaryProps> = ({
  displayGroup,
  className = "",
  isMainPanel = true,
  compareMode,
}) => {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("voiceek_ui");
  const { getAnalysis } = useAnalyses();
  const { displayGroups } = useDisplayGroups();
  const [isOpenVocView, setIsOpenVocView] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedProblemPath, setSelectedProblemPath] = useState<string>();

  const {
    selectedAnalysisId,
    selectedCategoryPath,
    analysisSelectOptions,
    isOpenCategorySelect,
    categoryTree,
    handleAnalysisChange,
    handleCategoryChange,
    handleOpenCategorySelect,
    handleCloseCategorySelect,
  } = useAnalysisCategorySelect(isMainPanel, 100);

  // 選択されたカテゴリが分析のカテゴリツリーに存在するかチェック
  const isCategoryValidForAnalysis = useMemo(() => {
    if (!selectedCategoryPath || !categoryTree) return false;
    return categoryTree.filterCategoriesByPaths([selectedCategoryPath]).length > 0;
  }, [selectedCategoryPath, categoryTree]);

  const { summary, isLoading } = useSummary(selectedAnalysisId, selectedCategoryPath, isCategoryValidForAnalysis);
  const { vocs } = useAnalysisVocs(selectedAnalysisId, selectedProblemPath);
  const analysis = getAnalysis(selectedAnalysisId);

  // 分析結果が空かどうかを判定（カテゴリが存在しない場合、カテゴリが存在するが分析結果がない場合の両方を「選択したカテゴリの分析結果はありません」と表示するため）
  const isSummaryEmpty = !summary
    || ((!summary.overview || summary.overview.trim() === "")
      && (!summary.problems || summary.problems.length === 0 || summary.problems.every(p => p.isEmptyProblem)));

  const {
    overviewRef,
  } = useHeightSync({
    isComparisionMode: Boolean(compareMode),
    compareMode,
    overview: summary?.overview,
  });

  const handleOpenVocView = (problem: string) => {
    setSelectedProblemPath(`${selectedCategoryPath}${problem}/`);
    setIsOpenVocView(true);
  };

  const handleDownloadCsv = async () => {
    if (!analysis) return;

    setIsDownloading(true);

    try {
      const data = await AnalysisService.downloadCsv(analysis.id, selectedCategoryPath);
      const fileName = `${analysis.year}${analysis.month}_${format(analysis.startedAt, "yyyyMMddHHmmss")}.csv`;
      downloadCsv(data, fileName);
    } catch (e) {
      toast.error(tCommon("errors.serverError"));
      console.error(e);
    }

    setIsDownloading(false);
  };

  return (
    <>
      <div className={`w-full flex flex-col flex-1 items-start gap-[30px] px-5 py-[30px] ${className}`}>
        <div className="bg-white h-full w-full rounded-[10px] overflow-hidden shadow-[0px_0px_10px_#00000040]">
          <CategoryHeader
            isMainPanel={isMainPanel}
            selectedAnalysisId={selectedAnalysisId}
            selectedCategoryPath={selectedCategoryPath}
            analysisSelectOptions={analysisSelectOptions}
            onAnalysisChange={handleAnalysisChange}
            onOpenCategorySelect={handleOpenCategorySelect}
          />

          <div className="inline-flex flex-col items-start w-full gap-2.5 p-5 mt-0">
            <div className="inline-flex flex-col items-start w-full gap-5 relative">
              {!selectedCategoryPath
                ? <EmptyState />
                : selectedCategoryPath && !isLoading && isSummaryEmpty
                ? <EmptyState message={t("analysis.categorySummary.categoryNotFound")} />
                : (
                  <>
                    <CategoryOverview
                      ref={overviewRef}
                      overview={summary?.overview}
                      isComparisionMode={Boolean(compareMode)}
                      overviewHeight={compareMode?.overviewHeight}
                      minOverviewHeight={compareMode?.minOverviewHeight}
                    />
                    <ProblemList
                      isMainPanel={isMainPanel}
                      totalCaseCount={summary?.totalCaseCount ?? 0}
                      isComparisionMode={Boolean(compareMode)}
                      selectedCategoryPath={selectedCategoryPath}
                      onOpenVocView={handleOpenVocView}
                    />
                  </>
                )}
            </div>

            {/* ダウンロードボタンは、summaryが存在し、かつ空でない場合のみ表示 */}
            {selectedAnalysisId && selectedCategoryPath && summary && !isSummaryEmpty && (
              <Button
                variant="outline"
                className={cn("self-end h-[34px]", compareMode ? "mt-2" : "mt-4")}
                onClick={handleDownloadCsv}
                disabled={isDownloading}
              >
                <Download />
                <span>{tCommon("button.download")}</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      <Modal open={isOpenVocView} onOpenChange={() => setIsOpenVocView(false)}>
        {vocs && <VocsView vocs={vocs} />}
      </Modal>

      {analysis && displayGroup && displayGroups && isOpenCategorySelect && (
        <CategorySelectModal
          analysis={analysis}
          displayGroups={displayGroups}
          initialDisplayGroup={displayGroup}
          initialCategoryPath={selectedCategoryPath}
          isOpenModal={isOpenCategorySelect}
          setIsOpenModal={handleCloseCategorySelect}
          submit={(_: string, categoryPath: string) => handleCategoryChange(categoryPath)}
        />
      )}
    </>
  );
};

export * from "./types";
